/**
 * Single source of truth for "this field accepts a column name or an inline
 * math.js expression."
 *
 * Threaded into every column-reference field across the pipeline schema and
 * the filter schema. Keeping it here avoids drift when the disambiguation
 * rule or the example expression set changes.
 *
 * Resolution lives in `inline-expressions.ts` — match-first against the
 * input dataset's column schema (plus user-supplied derive output names);
 * everything else parses as a math.js expression and evaluates per row.
 */
import z from 'zod';

const COLUMN_OR_EXPRESSION_DESCRIPTION =
  'A column reference. Accepts a literal column name (including dotted paths like `well.externalId`) ' +
  'or a math.js expression (e.g. `rate * 2`, `value(last(datapoints(rateRef)))`, ' +
  '`mean(values(monthly(rateRef)))`). Match-first: refs that match an input column are kept ' +
  'literal; everything else is parsed as a math.js expression and evaluated per row. ' +
  'Do NOT wrap the column name in quotes — pass `myColumn`, not `"myColumn"`.';

/**
 * True when the entire string is wrapped in matching quote characters at both
 * ends — `"foo"`, `'foo'`. Detects the common LLM mistake of treating a column
 * reference as a string literal. Internal quotes (`concat("a", "b")`) are
 * untouched because the start char `c` and the end char `)` don't match.
 */
function isQuoteWrapped(s: string): boolean {
  if (s.length < 2) {
    return false;
  }
  const first = s[0];
  const last = s.at(-1);
  return (first === '"' || first === "'") && first === last;
}

/** Single column reference. */
export const columnSchema = z
  .string()
  .refine((s) => !isQuoteWrapped(s), {
    message:
      'Column reference is wrapped in quotes (e.g. `"myColumn"`). Column names must be bare identifiers — pass `myColumn`, not `"myColumn"`. If you meant a math.js string literal, the column-reference field does not accept literal strings; use a column name or a math.js expression.',
  })
  .describe(COLUMN_OR_EXPRESSION_DESCRIPTION);

/** Array of column references; each entry follows the column-reference rules. */
export const columnsSchema = z
  .array(columnSchema)
  .describe(`Array of column references. ${COLUMN_OR_EXPRESSION_DESCRIPTION}`);
